import React, { useState, useEffect } from "react";
import api from "../api/axiosConfig";
import * as XLSX from "xlsx";
import "bootstrap/dist/css/bootstrap.min.css";

const MonthlyPerformanceReports = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [filters, setFilters] = useState({
    month: "",
    year: "",
    timeRange: "",
    startDate: "",
    endDate: "",
    role: "",
    location: "",
    name: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [data, filters]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/api/admin/all-performance-entries");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setError("Failed to fetch performance data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = data;

    // Date filters
    if (filters.month) {
      filtered = filtered.filter(
        (item) =>
          new Date(item.entry_date).getMonth() + 1 === parseInt(filters.month)
      );
    }

    if (filters.year) {
      filtered = filtered.filter(
        (item) =>
          new Date(item.entry_date).getFullYear() === parseInt(filters.year)
      );
    }

    if (filters.timeRange) {
      const now = new Date();
      let startDate;

      switch (filters.timeRange) {
        case "Last 7 Days":
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "Last 15 Days":
          startDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
          break;
        case "Last 30 Days":
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case "Custom Date Range":
          if (filters.startDate && filters.endDate) {
            const startDate = new Date(filters.startDate + "T00:00:00");
            const endDate = new Date(filters.endDate + "T23:59:59");
            filtered = filtered.filter((item) => {
              const itemDate = new Date(item.entry_date);
              return itemDate >= startDate && itemDate <= endDate;
            });
          }
          break;
        default:
          break;
      }

      if (startDate && filters.timeRange !== "Custom Date Range") {
        filtered = filtered.filter(
          (item) => new Date(item.entry_date) >= startDate
        );
      }
    }

    // User-specific filters
    if (filters.role) {
      filtered = filtered.filter((item) => item.role === filters.role);
    }

    if (filters.location) {
      filtered = filtered.filter((item) => item.location === filters.location);
    }

    if (filters.name) {
      filtered = filtered.filter((item) => item.username === filters.name);
    }

    setFilteredData(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };

      // Clear dependent filters when parent changes
      if (name === 'role') {
        newFilters.location = '';
        newFilters.name = '';
      } else if (name === 'location') {
        newFilters.name = '';
      }

      return newFilters;
    });
  };

  // Get filtered locations based on selected role
  const getFilteredLocations = () => {
    if (!filters.role) return [];
    return [...new Set(data.filter(item => item.role === filters.role).map(item => item.location).filter(loc => loc))].sort();
  };

  // Get filtered usernames based on selected role and location
  const getFilteredUsernames = () => {
    if (!filters.role || !filters.location) return [];
    return data.filter(item => item.role === filters.role && item.location === filters.location);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  const handleDownloadExcel = () => {
    downloadExcel();
    setShowDownloadModal(false);
  };

  const handleDownloadCSV = () => {
    downloadCSV();
    setShowDownloadModal(false);
  };

  const downloadExcel = () => {
    if (filteredData.length === 0) {
      alert("No data available to export");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Monthly Performance Reports"
    );
    XLSX.writeFile(workbook, "MonthlyPerformanceReports.xlsx");
  };

  const downloadCSV = () => {
    if (filteredData.length === 0) {
      alert("No data available to export");
      return;
    }

    // Convert data to CSV format
    const headers = [
      "ID",
      "Name",
      "UserName",
      "Role",
      "Report to",
      "Location",
      "Date",
      "Telephone",
      "Face to Face",
      "Appt Made",
      "Appt Kept",
      "Fact Find",
      "Service Reviews",
      "Social Calls",
      "HIW",
      "Referrals",
      "Closing Int.",
      "Sales Close",
      "Total Points",
    ];

    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) =>
        [
          row.id,
          `"${row.name}"`,
          `"${row.username}"`,
          `"${row.role}"`,
          `"${row.reports_to || 'N/A'}"`,
          `"${row.location || 'N/A'}"`,
          row.entry_date,
          row.telephoneSalesCalls || 0,
          row.faceToFace || 0,
          row.appointmentMade || 0,
          row.appointmentKept || 0,
          row.factFind || 0,
          row.serviceReviews || 0,
          row.socialCalls || 0,
          row.highIncomeWealthy || 0,
          row.referrals || 0,
          row.closingInterview || 0,
          row.salesClose || 0,
          row.points || 0,
        ].join(",")
      ),
    ].join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "MonthlyPerformanceReports.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container mt-4">
      <h2>Monthly Performance Reports</h2>

      <div className="row mb-3">
        <div className="col-md-3">
          <label className="form-label">Month</label>
          <select
            className="form-select"
            name="month"
            value={filters.month}
            onChange={handleFilterChange}
          >
            <option value="">All</option>
            <option value="1">January</option>
            <option value="2">February</option>
            <option value="3">March</option>
            <option value="4">April</option>
            <option value="5">May</option>
            <option value="6">June</option>
            <option value="7">July</option>
            <option value="8">August</option>
            <option value="9">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">Year</label>
          <input
            type="number"
            className="form-control"
            name="year"
            value={filters.year}
            onChange={handleFilterChange}
            placeholder="Enter year"
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Time Range</label>
          <select
            className="form-select"
            name="timeRange"
            value={filters.timeRange}
            onChange={handleFilterChange}
          >
            <option value="">All</option>
            <option value="Last 7 Days">Last 7 Days</option>
            <option value="Last 15 Days">Last 15 Days</option>
            <option value="Last 30 Days">Last 30 Days</option>
            <option value="Custom Date Range">Custom Date Range</option>
          </select>
        </div>
        <div className="col-md-3 d-flex align-items-end">
          <button
            className="btn btn-primary me-2"
            onClick={fetchData}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button
            className="btn btn-secondary me-2"
            onClick={() => setFilters({
              month: "",
              year: "",
              timeRange: "",
              startDate: "",
              endDate: "",
              role: "",
              location: "",
              name: "",
            })}
          >
            Clear Filters
          </button>
          <button
            className="btn btn-success"
            onClick={() => setShowDownloadModal(true)}
          >
            Download
          </button>
        </div>
      </div>

      {/* Cascading User Filters */}
      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label">Role</label>
          <select
            className="form-select"
            name="role"
            value={filters.role}
            onChange={handleFilterChange}
          >
            <option value="">Select Role</option>
            {[...new Set(data.map(item => item.role))].sort().map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>

        {filters.role && (
          <div className="col-md-4">
            <label className="form-label">Location</label>
            <select
              className="form-select"
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
            >
              <option value="">Select Location</option>
              {getFilteredLocations().map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>
        )}

        {filters.role && filters.location && (
          <div className="col-md-4">
            <label className="form-label">User Name</label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
              placeholder="Type or select username"
              list="username-list"
              autoComplete="off"
            />
            <datalist id="username-list">
              {[...new Set(getFilteredUsernames().map(item => `${item.name} (${item.username})`))].sort().map(userDisplay => {
                const username = userDisplay.match(/\(([^)]+)\)/)?.[1];
                return (
                  <option key={username} value={username} label={userDisplay} />
                );
              })}
            </datalist>
          </div>
        )}
      </div>

      {filters.timeRange === "Custom Date Range" && (
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-control"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">End Date</label>
            <input
              type="date"
              className="form-control"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
        </div>
      )}

      <div className="table-responsive" style={{ maxHeight: '70vh', overflow: 'auto' }}>
        <table className="table table-striped table-bordered">
          <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>UserName</th>
              <th>Role</th>
              <th>Report to</th>
              <th>Location</th>
              <th>Date</th>
              <th>Telephone</th>
              <th>Face to Face</th>
              <th>Appt Made</th>
              <th>Appt Kept</th>
              <th>Fact Find</th>
              <th>Service Reviews</th>
              <th>Social Calls</th>
              <th>HIW</th>
              <th>Referrals</th>
              <th>Closing Int.</th>
              <th>Sales Close</th>
              <th>Total Points</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item, index) => (
              <tr key={index}>
                <td>{item.id}</td>
                <td>{item.name}</td>
                <td>{item.username}</td>
                <td>{item.role}</td>
                <td>{item.reports_to || 'N/A'}</td>
                <td>{item.location || 'N/A'}</td>
                <td>{formatDate(item.entry_date)}</td>
                <td>{item.telephoneSalesCalls}</td>
                <td>{item.faceToFace}</td>
                <td>{item.appointmentMade}</td>
                <td>{item.appointmentKept}</td>
                <td>{item.factFind}</td>
                <td>{item.serviceReviews}</td>
                <td>{item.socialCalls}</td>
                <td>{item.highIncomeWealthy}</td>
                <td>{item.referrals}</td>
                <td>{item.closingInterview}</td>
                <td>{item.salesClose}</td>
                <td>{item.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {filteredData.length === 0 && !loading && !error && (
        <div className="alert alert-info" role="alert">
          No data available for the selected filters.
        </div>
      )}

      {/* Download Format Modal */}
      <div
        className={`modal fade ${showDownloadModal ? "show" : ""}`}
        style={{ display: showDownloadModal ? "block" : "none" }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Choose Download Format</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowDownloadModal(false)}
              ></button>
            </div>
            <div className="modal-body text-center">
              <p>
                Please select the format for downloading the performance
                reports:
              </p>
              <div className="d-flex justify-content-center gap-3">
                <button
                  className="btn btn-success btn-lg"
                  onClick={handleDownloadExcel}
                >
                  📊 Excel (.xlsx)
                </button>
                <button
                  className="btn btn-info btn-lg"
                  onClick={handleDownloadCSV}
                >
                  📄 CSV (.csv)
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDownloadModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Backdrop */}
      {showDownloadModal && (
        <div
          className="modal-backdrop fade show"
          onClick={() => setShowDownloadModal(false)}
        ></div>
      )}
    </div>
  );
};

export default MonthlyPerformanceReports;
